import { useState, useEffect, type ReactNode } from 'react';
import { QRCodeSVG } from 'qrcode.react';

interface DeviceFrameProps {
  children: ReactNode;
}

const FRAME_BREAKPOINT = 768;

export function DeviceFrame({ children }: DeviceFrameProps) {
  const [showFrame, setShowFrame] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth > FRAME_BREAKPOINT
  );

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${FRAME_BREAKPOINT + 1}px)`);
    const handler = (e: MediaQueryListEvent) => setShowFrame(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  if (!showFrame) {
    return <>{children}</>;
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-[#111827] overflow-hidden">
      {/* Background gradient */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 30% 20%, rgba(59,130,246,0.06) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(248,113,113,0.04) 0%, transparent 50%)',
        }}
      />
      {/* Subtle dot pattern */}
      <div
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />

      {/* Sidebar positioned to the left of the centered phone */}
      <div className="hidden xl:block absolute" style={{ right: 'calc(50% + 393px / 2 + 64px)' }}>
        <MarketingSidebar />
      </div>

      {/* iPhone 15 Pro frame — always centered */}
      <div className="relative" style={{ width: 393, height: 852 }}>
        {/* Outer titanium bezel */}
        <div
          className="absolute inset-0 rounded-[55px]"
          style={{
            background: 'linear-gradient(145deg, #3a3a3e 0%, #1a1a1e 50%, #2a2a2e 100%)',
            boxShadow: `
              0 0 0 1px rgba(255,255,255,0.06),
              0 30px 80px rgba(0,0,0,0.6),
              0 0 120px rgba(59,130,246,0.04),
              inset 0 0 0 1px rgba(255,255,255,0.04)
            `,
          }}
        />

        {/* Inner black bezel */}
        <div
          className="absolute rounded-[51px] bg-black"
          style={{ top: 3, left: 3, right: 3, bottom: 3 }}
        />

        {/* Screen area */}
        <div
          className="absolute overflow-hidden rounded-[47px]"
          style={{ top: 6, left: 6, right: 6, bottom: 6 }}
        >
          {/* Dynamic Island */}
          <div className="absolute top-0 left-0 right-0 z-50 flex justify-center pt-[10px] pointer-events-none">
            <div
              className="rounded-full"
              style={{
                width: 120,
                height: 35,
                background: '#000',
                boxShadow: '0 0 6px rgba(0,0,0,0.6)',
              }}
            />
          </div>

          {/* Status bar */}
          <div className="absolute top-0 left-0 right-0 z-40 flex items-center justify-between px-7 pointer-events-none" style={{ paddingTop: 14 }}>
            <StatusTime />
            <span />
            <StatusIcons />
          </div>

          {/* App content — device-screen class sets --sat/--sab CSS vars */}
          {/* contain: layout scopes fixed-position modals inside the frame */}
          <div className="device-screen w-full h-full overflow-hidden bg-slate-900" style={{ contain: 'layout' }}>
            {children}
          </div>

          {/* Home indicator */}
          <div className="absolute bottom-[6px] left-1/2 -translate-x-1/2 z-50 pointer-events-none">
            <div className="w-[134px] h-[5px] rounded-full bg-white/20" />
          </div>
        </div>

        {/* Side buttons — left */}
        <div className="absolute rounded-r-sm" style={{ left: -2, top: 160, width: 3, height: 26, background: 'linear-gradient(180deg, #3a3a3e, #2a2a2e)' }} />
        <div className="absolute rounded-r-sm" style={{ left: -2, top: 206, width: 3, height: 54, background: 'linear-gradient(180deg, #3a3a3e, #2a2a2e)' }} />
        <div className="absolute rounded-r-sm" style={{ left: -2, top: 272, width: 3, height: 54, background: 'linear-gradient(180deg, #3a3a3e, #2a2a2e)' }} />
        {/* Right — power */}
        <div className="absolute rounded-l-sm" style={{ right: -2, top: 230, width: 3, height: 86, background: 'linear-gradient(180deg, #3a3a3e, #2a2a2e)' }} />
      </div>
    </div>
  );
}

function MarketingSidebar() {
  return (
    <div className="flex flex-col items-start gap-8 relative z-10 max-w-xs">
      {/* App icon + name */}
      <div className="flex items-center gap-4">
        <img src="/icons/icon-192.png" alt="Rost & Found" className="w-16 h-16 rounded-2xl" />
        <div>
          <h1 className="text-2xl font-bold text-white">Rost & Found</h1>
          <p className="text-sm text-slate-400">Where crew connect</p>
        </div>
      </div>

      {/* Feature bullets */}
      <ul className="space-y-4 text-slate-300 text-[15px]">
        <li className="flex items-start gap-3">
          <FeatureIcon d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          Import your roster in seconds
        </li>
        <li className="flex items-start gap-3">
          <FeatureIcon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          See where your crew overlaps
        </li>
        <li className="flex items-start gap-3">
          <FeatureIcon d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          Message friends on layover
        </li>
      </ul>

      {/* Divider */}
      <div className="w-full h-px bg-white/10" />

      {/* App Store badge + QR code */}
      <div className="flex items-center gap-6">
        <a href="#" aria-label="Download on the App Store">
          <AppStoreBadge />
        </a>
        <div className="bg-white p-2 rounded-lg">
          <QRCodeSVG value="https://rostandfound.app" size={80} />
        </div>
      </div>
      <p className="text-xs text-slate-500 -mt-4">Scan to open on your phone</p>
    </div>
  );
}

function FeatureIcon({ d }: { d: string }) {
  return (
    <svg className="w-5 h-5 text-[#D4A373] shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

function AppStoreBadge() {
  return (
    <svg width="120" height="40" viewBox="0 0 120 40" xmlns="http://www.w3.org/2000/svg">
      <rect width="120" height="40" rx="6" fill="#000" />
      <rect x="0.5" y="0.5" width="119" height="39" rx="5.5" stroke="#A6A6A6" fill="none" />
      <g fill="#fff">
        <text x="37" y="12" fontSize="7" fontFamily="system-ui, -apple-system, sans-serif" letterSpacing="0.1">Download on the</text>
        <text x="37" y="27" fontSize="14" fontWeight="600" fontFamily="system-ui, -apple-system, sans-serif">App Store</text>
      </g>
      {/* Apple logo simplified */}
      <g transform="translate(14, 7)" fill="#fff">
        <path d="M11.2 9.2c0-2.7 2.2-4 2.3-4.1-1.3-1.8-3.2-2.1-3.9-2.1-1.6-.2-3.2 1-4 1s-2.1-1-3.5-1C.5 3 -1 4.1-1 6.6-1 9.2.5 13.4 2.2 15.8c.8 1.2 1.8 2.5 3.1 2.4 1.2-.1 1.7-.8 3.2-.8s1.9.8 3.2.8c1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.7 1.3-2.8-1.4-.5-2.8-2-2.8-4zM8.5 1.5C9.2.6 9.7-.6 9.5-1.8 8.5-1.7 7.2-1 6.5 0 5.8.8 5.2 2.1 5.5 3.2 6.5 3.3 7.7 2.5 8.5 1.5z" />
      </g>
    </svg>
  );
}

function StatusTime() {
  const [time, setTime] = useState(() => formatTime());
  useEffect(() => {
    const id = setInterval(() => setTime(formatTime()), 30_000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="text-white font-semibold" style={{ fontSize: 14, letterSpacing: 0.3 }}>
      {time}
    </span>
  );
}

function formatTime(): string {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function StatusIcons() {
  return (
    <svg width="68" height="12" viewBox="0 0 68 12" fill="none">
      {/* Signal bars */}
      <rect x="0" y="8" width="3" height="4" rx="0.5" fill="white" />
      <rect x="4.5" y="5.5" width="3" height="6.5" rx="0.5" fill="white" />
      <rect x="9" y="3" width="3" height="9" rx="0.5" fill="white" />
      <rect x="13.5" y="0" width="3" height="12" rx="0.5" fill="white" />
      {/* WiFi */}
      <path d="M26 8.5 L29.5 3.5 L33 8.5" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <circle cx="29.5" cy="10" r="1.2" fill="white" />
      {/* Battery */}
      <rect x="42" y="1.5" width="20" height="9" rx="2" stroke="white" strokeWidth="1" />
      <rect x="62" y="4" width="2" height="4" rx="0.5" fill="white" opacity="0.4" />
      <rect x="43.5" y="3" width="17" height="6" rx="1" fill="#34D399" />
    </svg>
  );
}
